import { Check, X, Info, Mail, MailOpen } from "lucide-react";
import { ICONS, CATEGORIES, ago, groupByDay, isPendingRequest } from "@/lib/notifications";

export function NotificationFilters({ filter, setFilter, counts, testPrefix = "notif" }) {
  const chip = (key, label, active, onClick) => (
    <button key={key} type="button" data-testid={`${testPrefix}-filter-${key}`} onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border whitespace-nowrap transition-colors ${active ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{label}</button>
  );
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testPrefix}-filters`}>
      {chip("all", "All", !filter.unread && !filter.cat, () => setFilter({ unread: false, cat: "" }))}
      {chip("unread", "Unread", filter.unread, () => setFilter({ ...filter, unread: !filter.unread }))}
      {CATEGORIES.filter(([k]) => counts[k]).map(([k, l]) => chip(k, l, filter.cat === k, () => setFilter({ ...filter, cat: filter.cat === k ? "" : k })))}
    </div>
  );
}

function NotificationItem({ n, onOpen, onToggleRead, onDismiss, onAct, touch }) {
  const Icon = ICONS[n.type] || Info;
  const pending = isPendingRequest(n);
  const resolved = n.type === "deletion_request" && n.meta?.request_status && n.meta.request_status !== "pending" ? n.meta.request_status : null;
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <div data-testid={`notification-item-${n.id}`} onClick={() => onOpen(n)}
      className={`group w-full text-left px-4 py-3 flex gap-3 border-b border-slate-50 transition-colors cursor-pointer hover:bg-slate-50 ${n.read ? "" : "bg-primary/5"}`}>
      <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${n.read ? "bg-slate-100 text-slate-400" : "bg-primary/10 text-primary"}`}><Icon className="h-4 w-4" /></div>
      <div className="min-w-0 flex-1">
        <div className={`text-sm truncate ${n.read ? "text-slate-600" : "font-semibold text-slate-900"}`}>{n.title}</div>
        <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-slate-400">{ago(n.created_at)}</span>
          {resolved && <span className={`text-[10px] font-semibold ${resolved === "approved" ? "text-emerald-600" : "text-slate-400"}`}>· {resolved}</span>}
        </div>
        {pending && (
          <div className="flex gap-2 mt-2">
            <button data-testid={`notif-approve-${n.id}`} onClick={stop(() => onAct(n, "approve"))} className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-2.5 py-1"><Check className="h-3 w-3" /> Approve</button>
            <button data-testid={`notif-decline-${n.id}`} onClick={stop(() => onAct(n, "decline"))} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 border border-slate-200 hover:bg-slate-100 rounded-lg px-2.5 py-1"><X className="h-3 w-3" /> Decline</button>
          </div>
        )}
      </div>
      <div className={`flex flex-col items-end gap-1 shrink-0 ${touch ? "" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"} transition-opacity`}>
        <button data-testid={`notif-toggle-read-${n.id}`} title={n.read ? "Mark as unread" : "Mark as read"} onClick={stop(() => onToggleRead(n))} className="h-6 w-6 rounded-md text-slate-400 hover:text-primary hover:bg-slate-100 flex items-center justify-center">
          {n.read ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
        </button>
        {!pending && <button data-testid={`notif-dismiss-${n.id}`} title="Remove" onClick={stop(() => onDismiss(n))} className="h-6 w-6 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>}
      </div>
      {!n.read && <span className="h-2 w-2 rounded-full bg-primary mt-2 shrink-0" />}
    </div>
  );
}

export function NotificationFeed({ items, empty = "You're all caught up.", ...handlers }) {
  if (!items.length) return <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="notif-empty">{empty}</div>;
  return groupByDay(items).map(([label, xs]) => (
    <div key={label} data-testid={`notif-group-${label.toLowerCase()}`}>
      <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-white sticky top-0 z-[1]">{label}</div>
      {xs.map((n) => <NotificationItem key={n.id} n={n} {...handlers} />)}
    </div>
  ));
}
